import React from 'react';
import { useParams } from 'react-router-dom';
import { SideBar } from '../../sidebar';
import i18n from '../../../../i18n';
import HeaderBanner from '../../../common/HeaderBanner';
import { formatDate } from '../../../../utils/common';
import { StatusNewBadge } from '../../../common/StatusBadge';
import { AUCTION_REQUEST_STATUS, AUCTION_REQUEST_TYPE } from '../../../../constants/status';
import { ArrowLeft, Expand, Shrink, ImageOff, Star } from 'lucide-react';
import '../../../../styles/components/create-group.scss';
import { getAuctionRequestDetail } from './services/auctionRequestService';

// NOTE: read-only detail view — no approve/reject/sync action here yet,
// since that backend endpoint doesn't exist (AuctionRequestController only
// handles YMS submitting create/update/delete requests so far). Let me
// know if you want approve/reject buttons wired up next; they'd need a
// new controller method + route first.
class AuctionRequestViewClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      request: null,
      isloading: true,
      notFound: false,
      hideSidebar: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.fetchDetail();
  }

  async fetchDetail() {
    const routeParams = this.props.params || {};
    const ymsAuctionId = routeParams.yms_auction_id ?? Object.values(routeParams)[0];

    if (!ymsAuctionId) {
      console.error('AuctionRequestView: no route param found. this.props.params =', routeParams);
      this.setState({ isloading: false, notFound: true });
      return;
    }

    this.setState({ isloading: true });
    try {
      const response = await getAuctionRequestDetail(ymsAuctionId);
      if (response?.success) {
        this.setState({ request: response.data, notFound: false });
      } else {
        this.setState({ notFound: true });
      }
    } catch (error) {
      console.error('Auction request detail fetch failed:', error);
      this.setState({ notFound: true });
    } finally {
      this.setState({ isloading: false });
    }
  }

  renderField(label, value) {
    return (
      <div className="form-field">
        <label>{label}</label>
        <div className="view-field-value">{value ?? value === 0 ? value : '-'}</div>
      </div>
    );
  }

  renderImages(images) {
    if (!images || !images.length) {
      return (
        <div className="attachment-empty">
          <ImageOff size={16} />
          <span>{i18n.t('no_images') || 'No images'}</span>
        </div>
      );
    }

    return (
      <div className="request-image-gallery" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {[...images]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((img) => (
            <div key={img.id} style={{ position: 'relative', width: '140px' }}>
              <img
                src={img.url || img.image_url}
                alt=""
                style={{ width: '140px', height: '100px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
              />
              {img.is_cover && (
                <span
                  style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    background: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Star size={11} fill="#fbbf24" color="#fbbf24" />
                  {i18n.t('cover') || 'Cover'}
                </span>
              )}
            </div>
          ))}
      </div>
    );
  }

  renderAttributes(attributes) {
    if (!attributes || !attributes.length) {
      return <div className="attachment-empty">{i18n.t('no_attributes') || 'No attributes'}</div>;
    }

    return (
      <table className="view-attributes-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>
              {i18n.t('attribute') || 'Attribute'}
            </th>
            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>
              {i18n.t('value') || 'Value'}
            </th>
          </tr>
        </thead>
        <tbody>
          {attributes.map((attr) => (
            <tr key={attr.id}>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                {attr.attribute?.name || `#${attr.attribute_id}`}
              </td>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{attr.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  render() {
    const { request, isloading, notFound, hideSidebar } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('auction_request_details') || 'Auction Request Details'}
          subtitle={i18n.t('review_auction_request_from_yms') || 'Details submitted by YMS for this auction'}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div
                className={hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'}
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      >
                        {hideSidebar && <Shrink strokeWidth={1} />}
                        {!hideSidebar && <Expand strokeWidth={1} />}
                      </span>
                    </div>
                    <div className="header-action">
                      <button
                        className="button1 button1-gradient button1-back"
                        onClick={() => window.history.back()}
                      >
                        <ArrowLeft size={18} className="icon" />
                        <span>{i18n.t('back')}</span>
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    {isloading ? (
                      <div className="attachment-empty">{i18n.t('loading') || 'Loading...'}</div>
                    ) : notFound || !request ? (
                      <div className="attachment-empty">
                        {i18n.t('auction_request_not_found') || 'Auction request not found'}
                      </div>
                    ) : (
                      <div className="create-group-container">
                        <div className="form-section">
                          <h2>
                            {request.title || request.yms_auction_id}{' '}
                            <StatusNewBadge status={AUCTION_REQUEST_STATUS[request.status]} />{' '}
                            <StatusNewBadge status={AUCTION_REQUEST_TYPE[request.request_type]} />
                          </h2>

                          <div className="form-row">
                            {this.renderField(i18n.t('yms_auction_id') || 'YMS Auction ID', request.yms_auction_id)}
                            {this.renderField(i18n.t('yms_group_id') || 'YMS Group ID', request.yms_group_id)}
                            {this.renderField('Title (EN)', request.title)}
                            {this.renderField('Title (AR)', request.title_ar)}
                          </div>

                          <div className="form-row">
                            {this.renderField(i18n.t('start_amount') || 'Start Amount', request.start_amount)}
                            {this.renderField(i18n.t('guarantee_amount') || 'Guarantee Amount', request.guarantee_amount)}
                            {this.renderField(i18n.t('bid_increment') || 'Bid Increment', request.bid_increment)}
                            {this.renderField(
                              i18n.t('increment_numbers') || 'Increment Options',
                              request.increment_numbers?.join(', ')
                            )}
                          </div>

                          <div className="form-row">
                            {this.renderField('Location (EN)', request.location)}
                            {this.renderField('Location (AR)', request.location_ar)}
                            {this.renderField(i18n.t('start_date') || 'Start Date', request.start_date ? formatDate(request.start_date) : '-')}
                            {this.renderField(i18n.t('end_date') || 'End Date', request.end_date ? formatDate(request.end_date) : '-')}
                          </div>

                          <div className="form-row">
                            {this.renderField(
                              i18n.t('reg_start_date') || 'Registration Start',
                              request.reg_start_date ? formatDate(request.reg_start_date) : '-'
                            )}
                            {this.renderField(
                              i18n.t('reg_end_date') || 'Registration End',
                              request.reg_end_date ? formatDate(request.reg_end_date) : '-'
                            )}
                            {this.renderField(i18n.t('submitted_on') || 'Submitted On', request.created_at ? formatDate(request.created_at) : '-')}
                            {this.renderField(i18n.t('last_updated') || 'Last Updated', request.updated_at ? formatDate(request.updated_at) : '-')}
                          </div>

                          {request.reason && (
                            <div className="form-row">
                              {this.renderField(i18n.t('reason') || 'Reason', request.reason)}
                            </div>
                          )}
                        </div>

                        <div className="form-section">
                          <h2>{i18n.t('images') || 'Images'}</h2>
                          {this.renderImages(request.images)}
                        </div>

                        <div className="form-section">
                          <h2>{i18n.t('attributes') || 'Attributes'}</h2>
                          {this.renderAttributes(request.attributes)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

// CHANGED: exporting this wrapper AS `AuctionRequestView` (named export —
// same name/import style as every other page in this app,
// `import { AuctionRequestView } from ...`) instead of a separate default
// export. Your route was rendering the raw class directly, so
// this.props.params was never being set at all and fetchDetail() bailed
// out before making any request. react-router v6 removed props.match —
// function components read the route param via useParams(), so the class
// needs this wrapper between it and the router either way.
export function AuctionRequestView(props) {
  const params = useParams();
  return <AuctionRequestViewClass {...props} params={params} />;
}